import { useEffect } from 'react'
import { ChevronRight } from 'lucide-react'
import type { RequirementNode, RequirementMetadata } from '@ispot/shared'
import { PersonaPortrait } from './cells'
import type { PersonaLookupEntry } from './usePersonaLookup'
import { DetailHeader } from './detail/DetailHeader'
import { DetailMetaStrip } from './detail/DetailMetaStrip'
import { DetailInsight } from './detail/DetailInsight'
import { NotesEditor } from './notes-editor/NotesEditor'
import { useState } from 'react'

interface RequirementDetailPanelProps {
  accountId: string
  projectId: string
  node: RequirementNode
  position?: { index: number; total: number }
  onUpdate: (id: string, data: Partial<RequirementNode>) => Promise<unknown>
  onDelete?: (id: string) => Promise<void>
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
  resolvePersona?: (name: string | undefined) => PersonaLookupEntry | null
}

export function RequirementDetailPanel({
  accountId, projectId, node, position,
  onUpdate, onDelete, onClose, onPrev, onNext, resolvePersona,
}: RequirementDetailPanelProps) {
  const meta = (node.metadata ?? {}) as RequirementMetadata
  const [dismissedInsight, setDismissedInsight] = useState(false)

  // ESC closes; ↑/↓ navigates when pane is open (mirrors proto behaviour).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      const isEditing = tag === 'TEXTAREA' || tag === 'INPUT' || target?.isContentEditable
      if (e.key === 'Escape' && !isEditing) { onClose(); return }
      if (e.key === 'ArrowDown' && !isEditing) { e.preventDefault(); onNext?.(); return }
      if (e.key === 'ArrowUp' && !isEditing) { e.preventDefault(); onPrev?.(); return }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, onPrev, onNext])

  const storedPersona = meta.linkedPersona
  const resolved = resolvePersona?.(storedPersona?.name)
  const linkedPersona = storedPersona
    ? {
        ...storedPersona,
        photo: storedPersona.photo || resolved?.photo,
        role: storedPersona.role || resolved?.role || '',
      }
    : undefined

  const isStory = node.type === 'story'
  const showPersonaFooter = isStory && linkedPersona
  const criteria = meta.acceptanceCriteria ?? []
  const suggestsSplit = criteria.length > 6

  return (
    <aside
      className="flex flex-col flex-shrink-0 detail-drawer-enter"
      style={{
        width: 460,
        borderLeft: '1px solid var(--color-border)',
        background: 'var(--color-warm-50)',
        height: '100%',
        overflow: 'hidden',
        boxShadow: '-8px 0 32px rgba(29,26,21,0.05), -2px 0 8px rgba(29,26,21,0.04)',
        animation: 'detailDrawerSlideIn 180ms cubic-bezier(0.2, 0.7, 0.2, 1)',
      }}
      aria-label="Requirement detail"
    >
      <DetailHeader
        node={node}
        position={position}
        onClose={onClose}
        onPrev={onPrev}
        onNext={onNext}
        onDelete={onDelete ? async () => { await onDelete(node.id); onClose() } : undefined}
      />

      <DetailMetaStrip
        node={node}
        onUpdate={(patch) => onUpdate(node.id, patch)}
      />

      <NotesEditor
        accountId={accountId}
        projectId={projectId}
        requirement={node}
        onUpdateRequirement={onUpdate}
      />

      {!dismissedInsight && suggestsSplit && (
        <DetailInsight
          criteriaCount={criteria.length}
          onDismiss={() => setDismissedInsight(true)}
        />
      )}

      {showPersonaFooter && (
        <section
          className="px-5 py-3 flex-shrink-0"
          style={{ borderTop: '1px solid var(--color-border-subtle)' }}
        >
          <button
            className="flex items-center gap-2.5 w-full py-2 px-2 rounded cursor-pointer bg-transparent border-0 text-left"
            style={{ transition: 'background 120ms' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <PersonaPortrait name={linkedPersona!.name} photo={linkedPersona!.photo} size={28} />
            <div className="flex-1 min-w-0">
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--color-fg-muted)',
                }}
              >
                Linked persona
              </div>
              <div className="text-[13px] truncate" style={{ color: 'var(--color-fg)' }}>
                {linkedPersona!.name}
                {linkedPersona!.role ? ` · ${linkedPersona!.role}` : ''}
              </div>
            </div>
            <ChevronRight style={{ width: 14, height: 14, color: 'var(--color-fg-muted)', flexShrink: 0 }} />
          </button>
        </section>
      )}
    </aside>
  )
}
